import React, { useEffect, useState, useMemo, useRef  } from 'react';
import {
  Container,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Modal,
  Box,
  TextField,
  Snackbar,
  Alert,
  Paper,
  Grid,
  InputAdornment,
  CircularProgress,
  Avatar
} from '@mui/material';
import { Edit, Delete, Add, Close, Search, FilterList, Clear, PhotoCamera, DirectionsCar } from '@mui/icons-material';
import axios from 'axios';
import { Visibility } from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';


const API_URL = process.env.REACT_APP_API_URL;

export default function Veiculos() {
  const [veiculos, setVeiculos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [detalhesModal, setDetalhesModal] = useState({ open: false, veiculo: null });
  const [fotoArquivo, setFotoArquivo] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoOriginal, setFotoOriginal] = useState(null); // URL original salva no DO Spaces
  const [uploadandoFoto, setUploadandoFoto] = useState(false);
  const fotoInputRef = useRef(null);

  const [formulario, setFormulario] = useState({
    placa: '',
    proprietario: '',
    modelo: '',
    renavam: '',
    chassi: '',
    preco_dia: '',
    preco_semana: '',
    preco_mes: '',
    foto_url: ''
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, mensagem: '', tipo: 'success' });
  const [erroModal, setErroModal] = useState({ open: false, mensagem: '' });
  const [confirmarModal, setConfirmarModal] = useState({ open: false, veiculoId: null });

  // Estados dos filtros
  const [filtros, setFiltros] = useState({
    placa: '',
    proprietario: '',
    modelo: '',
    renavam: '',
    chassi: '',
    precoMin: '',
    precoMax: ''
  });

  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  // Veículos filtrados
  const veiculosFiltrados = useMemo(() => {
    return veiculos.filter((veiculo) => {
      const matchPlaca = !filtros.placa || 
        veiculo.placa?.toLowerCase().includes(filtros.placa.toLowerCase());
      
      const matchProprietario = !filtros.proprietario || 
        veiculo.proprietario?.toLowerCase().includes(filtros.proprietario.toLowerCase());
      
      const matchModelo = !filtros.modelo || 
        veiculo.modelo?.toLowerCase().includes(filtros.modelo.toLowerCase());
      
      const matchRenavam = !filtros.renavam || 
        veiculo.renavam?.toLowerCase().includes(filtros.renavam.toLowerCase());
      
      const matchChassi = !filtros.chassi || 
        veiculo.chassi?.toLowerCase().includes(filtros.chassi.toLowerCase());

      const precoMinNum = parseFloat(filtros.precoMin) || 0;
      const precoMaxNum = parseFloat(filtros.precoMax) || Infinity;
      const matchPreco = veiculo.preco_dia >= precoMinNum && veiculo.preco_dia <= precoMaxNum;

      return matchPlaca && matchProprietario && matchModelo && matchRenavam && matchChassi && matchPreco;
    });
  }, [veiculos, filtros]);

  const handleLimparFiltros = () => {
    setFiltros({
      placa: '',
      proprietario: '',
      modelo: '',
      renavam: '',
      chassi: '',
      precoMin: '',
      precoMax: ''
    });
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros({ ...filtros, [campo]: valor });
  };

  const formatarReal = (valor) => {
    const numero = parseFloat(valor);
    if (isNaN(numero)) return 'R$ 0,00';
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const exibirPlaca = (placa) => {
    const raw = placa?.toUpperCase().replace(/[^A-Z0-9]/g, '') || '';
    if (raw.length === 7) {
      const isMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(raw);
      return isMercosul ? raw : `${raw.slice(0, 3)}-${raw.slice(3)}`;
    }
    return raw;
  };

/* eslint-disable react/prop-types */
const columns = useMemo(() => [
{ accessorKey: 'id', header: 'ID', size: 20, maxSize: 25, minSize: 60 },
  {
    id: 'foto',
    header: 'Foto',
    size: 60,
    Cell: ({ row }) => (
      row.original.foto_url
        ? <Avatar src={row.original.foto_url} variant="rounded" sx={{ width: 48, height: 36 }} />
        : <Avatar variant="rounded" sx={{ width: 48, height: 36, bgcolor: '#f0f0f0' }}><DirectionsCar sx={{ color: '#bbb', fontSize: 20 }} /></Avatar>
    )
  },
  {
    accessorFn: (row) => `${exibirPlaca(row.placa)}/${row.proprietario}/${row.modelo}`,
    id: 'nome',
    header: 'Nome',
    filterVariant: 'text'
  },
  { accessorKey: 'renavam', header: 'Renavam', filterVariant: 'text' },
  { accessorKey: 'chassi', header: 'Chassi', filterVariant: 'text' },
  {
    accessorKey: 'preco_dia',
    header: 'Preço Dia',
    Cell: ({ cell }) => formatarReal(cell.getValue()),
  },
  {
    accessorKey: 'preco_semana',
    header: 'Preço Semana',
    Cell: ({ cell }) => formatarReal(cell.getValue()),
  },
  {
    accessorKey: 'preco_mes',
    header: 'Preço Mês',
    Cell: ({ cell }) => formatarReal(cell.getValue()),
  },
  {
    id: 'acoes',
    header: 'Ações',
    Cell: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Editar">
          <IconButton onClick={() => handleAbrirModal(row.original)}>
            <Edit sx={{ color: "#f78b00" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir">
          <IconButton color="error" onClick={() => setConfirmarModal({ open: true, veiculoId: row.original.id })}>
            <Delete />
          </IconButton>
        </Tooltip>
        <Tooltip title="Visualizar">
          <IconButton onClick={() => setDetalhesModal({ open: true, veiculo: row.original })}>
            <Visibility sx={{ color: "#0070f3" }} />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }
], []);
/* eslint-enable react/prop-types */
const table = useMaterialReactTable({
  columns,
  data: veiculosFiltrados,
  localization: MRT_Localization_PT_BR,
  enableColumnFilters: false,
  enableGlobalFilter: false,
  enablePagination: true,
  enableSorting: true,
 initialState: {
  pagination: { pageSize: 50, pageIndex: 0 },
  sorting: [{ id: 'id', desc: true }]
},

  muiTablePaperProps: {
    elevation: 3,
    sx: { borderRadius: 2, mt: 3 }
  },
  muiTableContainerProps: {
    sx: { maxHeight: 'auto', overflowY: 'auto' }
  },
  muiTableHeadCellProps: {
    sx: { backgroundColor: "#f9f9f9", fontWeight: 'bold', color: '#333' }
  },
});

  const fetchVeiculos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/veiculos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVeiculos(res.data);
    } catch {
      setSnackbar({ open: true, mensagem: 'Erro ao carregar veículos.', tipo: 'error' });
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchVeiculos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [erros, setErros] = useState({});



  const handleAbrirModal = (veiculo = null) => {
    if (veiculo) {
      setFormulario({
        ...veiculo,
        preco_dia: formatReal((veiculo.preco_dia || 0).toString()),
        preco_semana: formatReal((veiculo.preco_semana || 0).toString()),
        preco_mes: formatReal((veiculo.preco_mes || 0).toString()),
        foto_url: veiculo.foto_url || ''
      });
      setFotoPreview(veiculo.foto_url || null);
      setFotoOriginal(veiculo.foto_url || null);
    } else {
      setFormulario({
        placa: '',
        proprietario: '',
        modelo: '',
        renavam: '',
        chassi: '',
        preco_dia: '',
        preco_semana: '',
        preco_mes: '',
        foto_url: ''
      });
      setFotoPreview(null);
    }
  
    setFotoArquivo(null);
    setErros({});
    setModalOpen(true);
  };
  
  const handleFecharModal = () => {
    setFormulario({
      nome: '',
      placa: '',
      renavam: '',
      chassi: '',
      preco_dia: '',
      preco_semana: '',
      preco_mes: '',
      foto_url: ''
    });
    setFotoArquivo(null);
    setFotoPreview(null);
    setFotoOriginal(null);
    setErros({});
    setModalOpen(false);
  };
  


  const isSpacesUrl = (url) =>
    url && (url.includes('digitaloceanspaces.com') || (process.env.REACT_APP_DO_CDN && url.startsWith(process.env.REACT_APP_DO_CDN)));

  const deletarFotoNoSpaces = async (url) => {
    if (!isSpacesUrl(url)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/upload/file-by-url`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { url }
      });
    } catch (err) {
      console.warn('Não foi possível remover foto antiga do Spaces:', err.message);
    }
  };

  const handleSalvar = async () => {
    try {
      const token = localStorage.getItem('token');
  
      const novosErros = {};
      const obrigatorios = ['placa', 'proprietario', 'modelo', 'renavam'];
  
      obrigatorios.forEach((campo) => {
        if (!formulario[campo] || !formulario[campo].toString().trim()) {
          novosErros[campo] = `O campo ${campo.toUpperCase()} é obrigatório.`;
        }
      });
  
      if (Object.keys(novosErros).length > 0) {
        setErros(novosErros);
        setSnackbar({
          open: true,
          mensagem: 'Preencha todos os campos obrigatórios.',
          tipo: 'error'
        });
        return;
      }
  
      setErros({}); // Limpa os erros se passou
  
      const parsePreco = (valor) =>
        parseFloat(valor.replace('R$', '').replace(/\./g, '').replace(',', '.')) || 0;
  
      const precoDia = parsePreco(formulario.preco_dia);
      const precoSemana = parsePreco(formulario.preco_semana);
      const precoMes = parsePreco(formulario.preco_mes);
  
      if ([precoDia, precoSemana, precoMes].some(val => val > 99999999.99)) {
        setSnackbar({
          open: true,
          mensagem: 'Valor excede o limite permitido (99.999.999,99)',
          tipo: 'error'
        });
        return;
      }
  
      const dadosParaSalvar = {
        ...formulario,
        preco_dia: precoDia,
        preco_semana: precoSemana,
        preco_mes: precoMes,
      };

      // Upload da foto se houver arquivo novo selecionado
      if (fotoArquivo) {
        setUploadandoFoto(true);
        try {
          const formData = new FormData();
          formData.append('foto', fotoArquivo);
          // Passa a URL antiga para o backend deletar do Spaces após o upload bem-sucedido
          if (fotoOriginal && isSpacesUrl(fotoOriginal)) {
            formData.append('old_url', fotoOriginal);
          }
          const resUpload = await axios.post(`${API_URL}/upload/veiculo-foto`, formData, {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (evt) => {
              const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
              setSnackbar({ open: true, mensagem: `Enviando foto... ${pct}%`, tipo: 'info' });
            }
          });
          dadosParaSalvar.foto_url = resUpload.data.url;
          setFotoOriginal(resUpload.data.url);
          setSnackbar({ open: false, mensagem: '', tipo: 'info' });
        } catch (uploadErr) {
          setUploadandoFoto(false);
          const msg = uploadErr.response?.data?.erro
            || uploadErr.response?.data?.mensagem
            || uploadErr.response?.data?.detalhes
            || uploadErr.message
            || 'Erro ao fazer upload da foto.';
          setSnackbar({ open: true, mensagem: `Upload falhou: ${msg}`, tipo: 'error' });
          return;
        }
        setUploadandoFoto(false);
      }
  
      const url = formulario.id
        ? `${API_URL}/veiculos/${formulario.id}`
        : `${API_URL}/veiculos`;
  
      const method = formulario.id ? 'put' : 'post';
  
      await axios[method](url, dadosParaSalvar, {
        headers: { Authorization: `Bearer ${token}` }
      });
  
      fetchVeiculos();
      handleFecharModal();
      setSnackbar({ open: true, mensagem: 'Veículo salvo com sucesso!', tipo: 'success' });
    } catch (err) {
      console.error('Erro ao salvar veículo:', err);
      const mensagemErro = err.response?.data?.mensagem || 'Erro ao salvar veículo.';
      setSnackbar({ open: true, mensagem: mensagemErro, tipo: 'error' });
    }
  };
  
  
  
  

  const formatReal = (value) => {
    const num = value.replace(/\D/g, '').slice(0, 11);
    const float = (parseInt(num || '0') / 100).toFixed(2);
    return `R$ ${float}`.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };
  
  const formatPlaca = (valor) => {
    const raw = valor.toUpperCase().replace(/[^A-Z0-9]/g, '');
  
    if (raw.length <= 3) return raw;
    if (raw.length <= 4) return raw.slice(0, 3) + raw[3];
    if (raw.length <= 5) return raw.slice(0, 3) + raw[3] + raw[4];
    if (raw.length <= 6) return raw.slice(0, 3) + raw[3] + raw[4] + raw[5];
    if (raw.length === 7) {
      const isMercosul = /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(raw);
      return isMercosul
        ? raw // Exibe como está: AAA0A00
        : `${raw.slice(0, 3)}-${raw.slice(3)}`; // Ex: ABC-1234
    }
  
    return raw.slice(0, 7);
  };
  

  
  const handleExcluir = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/veiculos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
  
      fetchVeiculos();
      setConfirmarModal({ open: false, veiculoId: null });
      setSnackbar({ open: true, mensagem: 'Veículo excluído com sucesso!', tipo: 'success' });
    } catch (err) {
      console.error('Erro ao excluir veículo:', err);
      const mensagemErro = err.response?.data?.mensagem || 'Erro ao excluir veículo.';
      setConfirmarModal({ open: false, veiculoId: null });
      setErroModal({ open: true, mensagem: mensagemErro });
    }
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    let novoValor = value.toUpperCase(); // Sempre transforma em caixa alta
  
    if (name === 'placa') {
      novoValor = formatPlaca(novoValor); // Mantém formatação da placa
    }
  
    if (['preco_dia', 'preco_semana', 'preco_mes'].includes(name)) {
      novoValor = formatReal(novoValor); // Aplica formatação de preço
    }
  
    setFormulario({ ...formulario, [name]: novoValor });
  };
  
  return (
    <Container maxWidth="" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h6" sx={{ mt: 5, mb: 3 }}>
        Veículos
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<Add sx={{ color: '#f78b00' }} />}
          onClick={() => handleAbrirModal()}
          sx={{
            backgroundColor: "#1f1f1f",
            fontWeight: "bold",
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "#333",
              boxShadow: "0 0 0 2px #f78b00 inset"
            }
          }}
        >
          Adicionar
        </Button>

        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          sx={{
            borderColor: "#f78b00",
            color: "#f78b00",
            fontWeight: "bold",
            "&:hover": {
              borderColor: "#ff9922",
              bgcolor: "rgba(247, 139, 0, 0.04)"
            }
          }}
        >
          {mostrarFiltros ? 'Ocultar' : 'Mostrar'} Filtros
        </Button>

        {Object.values(filtros).some(v => v !== '') && (
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={handleLimparFiltros}
            sx={{
              color: "#666",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.04)"
              }
            }}
          >
            Limpar Filtros
          </Button>
        )}

        <Typography variant="body2" sx={{ ml: 'auto', color: '#666', fontWeight: 'bold' }}>
          {veiculosFiltrados.length} de {veiculos.length} veículos ativos
        </Typography>
      </Box>

      {/* PAINEL DE FILTROS */}
      {mostrarFiltros && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            border: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            pb: 2,
            borderBottom: '2px solid #f78b00'
          }}>
            <Search sx={{ color: '#f78b00', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
              Filtros Avançados
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Placa"
                value={filtros.placa}
                onChange={(e) => handleFiltroChange('placa', e.target.value.toUpperCase())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Proprietário"
                value={filtros.proprietario}
                onChange={(e) => handleFiltroChange('proprietario', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Modelo"
                value={filtros.modelo}
                onChange={(e) => handleFiltroChange('modelo', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Renavam"
                value={filtros.renavam}
                onChange={(e) => handleFiltroChange('renavam', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Chassi"
                value={filtros.chassi}
                onChange={(e) => handleFiltroChange('chassi', e.target.value.toUpperCase())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#999', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Preço Mínimo (dia)"
                type="number"
                value={filtros.precoMin}
                onChange={(e) => handleFiltroChange('precoMin', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#999', fontSize: 14 }}>R$</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Preço Máximo (dia)"
                type="number"
                value={filtros.precoMax}
                onChange={(e) => handleFiltroChange('precoMax', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#999', fontSize: 14 }}>R$</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: '#fff',
                    "&:hover fieldset": { borderColor: "#f78b00" },
                    "&.Mui-focused fieldset": { borderColor: "#f78b00" }
                  }
                }}
              />
            </Grid>
          </Grid>

          {Object.values(filtros).some(v => v !== '') && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Clear />}
                onClick={handleLimparFiltros}
                sx={{
                  borderColor: '#999',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#666',
                    bgcolor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Limpar Todos os Filtros
              </Button>
            </Box>
          )}
        </Paper>
      )}

      <MaterialReactTable table={table} />


      <Modal open={modalOpen} onClose={handleFecharModal}>
      <Box
  sx={{
    width: "90%",
    maxWidth: 600,
    bgcolor: "#ffffff",
    borderRadius: 3,
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  }}
>
          {/* HEADER COM GRADIENTE */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#fff" }}>
              {formulario.id ? "✏️ Editar Veículo" : "➕ Adicionar Veículo"}
            </Typography>
            <IconButton
              onClick={handleFecharModal}
              sx={{ color: "#fff", '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>

            {/* FOTO DO VEÍCULO */}
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                onClick={() => !uploadandoFoto && fotoInputRef.current?.click()}
                sx={{
                  width: 180,
                  height: 120,
                  borderRadius: 2,
                  border: '2px dashed #f78b00',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: uploadandoFoto ? 'not-allowed' : 'pointer',
                  overflow: 'hidden',
                  bgcolor: '#f8f9fa',
                  '&:hover': { borderColor: '#ff9922', bgcolor: '#fff3e0' },
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
              >
                {uploadandoFoto ? (
                  <Box sx={{ textAlign: 'center', color: '#f78b00' }}>
                    <CircularProgress size={36} sx={{ color: '#f78b00', mb: 0.5 }} />
                    <Typography variant="caption" display="block">Enviando...</Typography>
                  </Box>
                ) : fotoPreview ? (
                  <img
                    src={fotoPreview}
                    alt="Foto do veículo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box sx={{ textAlign: 'center', color: '#999' }}>
                    <PhotoCamera sx={{ fontSize: 36, mb: 0.5 }} />
                    <Typography variant="caption" display="block">Clique para adicionar foto</Typography>
                  </Box>
                )}
              </Box>
              {fotoPreview && (
                <Button
                  size="small"
                  color="error"
                  onClick={async () => {
                    // Se a foto era de um veículo já salvo (não só preview local), deleta do Spaces
                    if (fotoOriginal && isSpacesUrl(fotoOriginal)) {
                      await deletarFotoNoSpaces(fotoOriginal);
                    }
                    setFotoPreview(null);
                    setFotoArquivo(null);
                    setFotoOriginal(null);
                    setFormulario(f => ({ ...f, foto_url: '' }));
                  }}
                >
                  Remover foto
                </Button>
              )}
              <input
                ref={fotoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFotoArquivo(file);
                    setFotoPreview(URL.createObjectURL(file));
                  }
                  e.target.value = '';
                }}
              />
            </Box>
            {["placa", "proprietario", "modelo", "renavam", "chassi", "preco_dia", "preco_semana", "preco_mes"].map((field, i) => (
         <TextField
         key={i}
         fullWidth
         label={field.replace('_', ' ').toUpperCase()}
         name={field}
         value={formulario[field]}
         onChange={handleChange}
         error={!!erros[field]}
         helperText={erros[field] || ''}
         sx={{
           mb: 2,
           "& .MuiInputBase-input": { 
             color: "#333",
             fontSize: '0.95rem'
           },
           "& .MuiInputLabel-root": { 
             color: "#666",
             '&.Mui-focused': { color: '#f78b00' }
           },
           "& .MuiOutlinedInput-root": {
             backgroundColor: '#f8f9fa',
             borderRadius: 1,
             "& fieldset": { 
               borderColor: erros[field] ? "#f44336" : "#e0e0e0"
             },
             "&:hover fieldset": { 
               borderColor: erros[field] ? "#f44336" : "#f78b00"
             },
             "&.Mui-focused fieldset": { 
               borderColor: "#f78b00",
               borderWidth: 2
             }
           }
         }}
       />
          ))}
          </Box>

          {/* BOTÕES */}
          <Box sx={{
            p: 3,
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa'
          }}>
            <Button
              variant="text"
              onClick={handleFecharModal}
              sx={{ color: '#333' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSalvar}
              disabled={uploadandoFoto}
              startIcon={uploadandoFoto ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
                color: "#fff",
                fontWeight: "bold",
                "&:hover": {
                  background: 'linear-gradient(135deg, #2a2a2a 0%, #ff9922 100%)'
                }
              }}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={detalhesModal.open} onClose={() => setDetalhesModal({ open: false, veiculo: null })}>
        <Box sx={{
          width: "90%", 
          maxWidth: 600, 
          bgcolor: "#fff", 
          p: 0,
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          position: "absolute", 
          top: "50%", 
          left: "50%",
          transform: "translate(-50%, -50%)",
          overflow: 'hidden'
        }}>
          {/* HEADER */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#fff" }}>
              👁️ Detalhes do Veículo
            </Typography>
            <IconButton
              onClick={() => setDetalhesModal({ open: false, veiculo: null })}
              sx={{ color: "#fff", '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* CONTEÚDO */}
          {detalhesModal.veiculo && (
            <Box sx={{ p: 3 }}>
              {/* FOTO */}
              {detalhesModal.veiculo.foto_url && (
                <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', maxHeight: 200, display: 'flex', justifyContent: 'center', bgcolor: '#000' }}>
                  <img
                    src={detalhesModal.veiculo.foto_url}
                    alt="Foto do veículo"
                    style={{ width: '100%', height: 200, objectFit: 'cover' }}
                  />
                </Box>
              )}
              <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>PLACA</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f78b00' }}>
                  {exibirPlaca(detalhesModal.veiculo.placa)}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box sx={{ pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>PROPRIETÁRIO</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {detalhesModal.veiculo.proprietario}
                  </Typography>
                </Box>
                <Box sx={{ pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>MODELO</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {detalhesModal.veiculo.modelo}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box sx={{ pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>RENAVAM</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {detalhesModal.veiculo.renavam}
                  </Typography>
                </Box>
                <Box sx={{ pb: 2, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>CHASSI</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                    {detalhesModal.veiculo.chassi}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', mb: 1.5 }}>
                  💰 TABELA DE PREÇOS
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Diária</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f78b00' }}>
                      {formatarReal(detalhesModal.veiculo.preco_dia)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Semanal</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f78b00' }}>
                      {formatarReal(detalhesModal.veiculo.preco_semana)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Mensal</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f78b00' }}>
                      {formatarReal(detalhesModal.veiculo.preco_mes)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* RODAPÉ */}
          <Box sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa'
          }}>
            <Button
              variant="contained"
              onClick={() => setDetalhesModal({ open: false, veiculo: null })}
              sx={{
                background: 'linear-gradient(135deg, #1f1f1f 0%, #f78b00 100%)',
                color: "#fff",
                fontWeight: "bold"
              }}
            >
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.tipo} sx={{ width: "100%" }}>
          {snackbar.mensagem}
        </Alert>
      </Snackbar>

      {/* MODAL DE ERRO */}
      <Modal open={erroModal.open} onClose={() => setErroModal({ open: false, mensagem: '' })}>
        <Box sx={{
          width: "90%",
          maxWidth: 500,
          bgcolor: "#ffffff",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          overflow: 'hidden'
        }}>
          {/* HEADER */}
          <Box sx={{
            background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#fff" }}>
              ❌ Erro ao Excluir
            </Typography>
            <IconButton
              onClick={() => setErroModal({ open: false, mensagem: '' })}
              sx={{ color: "#fff", '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* CONTEÚDO */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ color: '#333', mb: 2 }}>
              {erroModal.mensagem}
            </Typography>
          </Box>

          {/* RODAPÉ */}
          <Box sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa'
          }}>
            <Button
              variant="contained"
              onClick={() => setErroModal({ open: false, mensagem: '' })}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: "#fff",
                fontWeight: "bold",
                "&:hover": {
                  background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)'
                }
              }}
            >
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
      <Modal open={confirmarModal.open} onClose={() => setConfirmarModal({ open: false, veiculoId: null })}>
        <Box sx={{
          width: "90%",
          maxWidth: 500,
          bgcolor: "#ffffff",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          overflow: 'hidden'
        }}>
          {/* HEADER */}
          <Box sx={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#fff" }}>
              ⚠️ Confirmar Exclusão
            </Typography>
            <IconButton
              onClick={() => setConfirmarModal({ open: false, veiculoId: null })}
              sx={{ color: "#fff", '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* CONTEÚDO */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ fontSize: '4rem', mb: 2, color: '#ff9800' }}>
              🗑️
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 3 }}>
              Tem certeza que deseja excluir este veículo?
            </Typography>

            {/* Aviso sobre preservação do histórico */}
            <Box sx={{
              bgcolor: '#e8f5e9',
              border: '1px solid #a5d6a7',
              borderRadius: 2,
              p: 2,
              mb: 2,
              textAlign: 'left',
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start'
            }}>
              <Box sx={{ fontSize: '1.4rem', lineHeight: 1 }}>✅</Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32', mb: 0.5 }}>
                  Histórico de aluguéis preservado
                </Typography>
                <Typography variant="body2" sx={{ color: '#388e3c' }}>
                  Todos os aluguéis e relatórios vinculados a este veículo continuarão disponíveis normalmente, incluindo dados financeiros e histórico completo de locações.
                </Typography>
              </Box>
            </Box>

            {/* Aviso sobre o que muda */}
            <Box sx={{
              bgcolor: '#fff3e0',
              border: '1px solid #ffcc80',
              borderRadius: 2,
              p: 2,
              textAlign: 'left',
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start'
            }}>
              <Box sx={{ fontSize: '1.4rem', lineHeight: 1 }}>⚠️</Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100', mb: 0.5 }}>
                  O veículo deixará de aparecer na listagem
                </Typography>
                <Typography variant="body2" sx={{ color: '#bf360c' }}>
                  Após excluído, este veículo não aparecerá mais na lista de veículos nem poderá ser selecionado para novos aluguéis.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* RODAPÉ */}
          <Box sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            borderTop: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa'
          }}>
            <Button
              variant="outlined"
              onClick={() => setConfirmarModal({ open: false, veiculoId: null })}
              sx={{
                color: '#333',
                borderColor: '#ccc',
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: '#999',
                  bgcolor: '#f0f0f0'
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => handleExcluir(confirmarModal.veiculoId)}
              sx={{
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: "#fff",
                fontWeight: "bold",
                "&:hover": {
                  background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)'
                }
              }}
            >
              Excluir Veículo
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}
